import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import EmptyState from "../../lib/components/EmptyState";
import {
  Alert,
  Box,
  Grid,
  Button,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import transmissionService from "../../service/transmissionService";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getCount, refetchAtInterval, successCheck } from "../../utils";
import moment from "moment";
import { moneyFormat } from "../../utils/moneyFormat";
import PreloaderTable from "../../lib/components/PreloaderTable";
import { theme } from "../../App";
import AppLayout from "../../lib/components/Layout/app-layout";
import Header from "../../lib/components/Header";
import organizationservice from "../../service/organizationservice";
import { ArrowBack, Close, Search } from "@mui/icons-material";
import useDebounce from "../../hooks/useDebounce";
import { MdDownload, MdExpandMore } from "react-icons/md";
import { FaClipboardList, FaCloudUploadAlt, FaFileInvoice, FaQrcode } from "react-icons/fa";
import { BiRefresh, BiSolidWallet } from "react-icons/bi";
import scrollHandler from "../../utils/scrollHandler";
import AppSelectField from "../../lib/components/AppSelectField";
import { ViewTimelineModal } from "./ViewTransmission";
import AppTextField from "../../lib/components/AppTextField";
import DateRangePreset, { computePresetRange, DATE_PRESETS, normalizeDatePreset } from "./components/DateRangePreset";
import SortFilterMenu from "./components/SortFilterMenu";
import { useSSEWithRefetch } from "../../hooks/useSSEWithRefetch";
import errorHandler from "../../utils/errorHandler";
import { toast } from "react-toastify";

const defaultStatus = "TRANSMISSION_FAILED,TRANSMISSION_INITIATED,TRANSMISSION_COMPLETED";

const STATUS_FILTER_ITEMS = [
  { label: "All", value: defaultStatus },
  { label: "Initiated", value: "TRANSMISSION_INITIATED" },
  { label: "Completed", value: "TRANSMISSION_COMPLETED" },
  { label: "Failed", value: "TRANSMISSION_FAILED" },
];

const TransmittedInvoices = () => {
  const { id: requestId } = useParams();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const page = searchParams.get("page") || "1";
  const limit = searchParams.get("limit") || "20";
  const keyword = searchParams.get("keyword") || "";
  const status = searchParams.get("status") || defaultStatus;
  const urlDatePreset = searchParams.get("datePreset");
  const dateFrom = searchParams.get("dateFrom") || null;
  const dateTo = searchParams.get("dateTo") || null;
  const organizationId = searchParams.get("organizationId") || null;

  const searchTerm = useDebounce(keyword);
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectedInvoice, setSelectedInvoice] = React.useState(null);

  // CUSTOM is the default so no date params are sent unless the user picks a date range.
  const datePreset = normalizeDatePreset(urlDatePreset || DATE_PRESETS.CUSTOM);
  // transmittedInvoices API only accepts dateFrom/dateTo (no datePreset param).
  // No date in URL → no date params in the payload.
  const queries = {
    page,
    limit,
    id: organizationId,
    requestId,
    keyword: searchTerm,
    status,
    ...(dateFrom ? { dateFrom } : {}),
    ...(dateTo ? { dateTo } : {}),
  };

  const transmissionQuery = useQuery({
    retry: false,
    queryKey: [`transmitted-invoices-${requestId}`, ...Object.values(queries)],
    queryFn: () => organizationservice.transmittedInvoices({ ...queries }),
  });

  const retryMutation = useMutation({
    mutationFn: () => transmissionService.retrySyncAll({ requestId, organizationId }),
    onSuccess({}) {
      refetchAtInterval(transmissionQuery, 3000, 5);
      toast.success("Syncing transmission requests...");
    },
    onError: ({ response }) => errorHandler(response),
  });

  const transmissionSummaryQuery = useQuery({
    retry: false,
    queryKey: ["transmission-summary"],
    queryFn: () => transmissionService.trendsSummary({ organizationId, requestId }),
  });

  const invoices = transmissionQuery?.data?.data;
  const transmissionCount = transmissionSummaryQuery?.data?.data?.data;

  const stats = {
    "Total Transmissions": {
      count: transmissionCount?.totalTransmissions ?? 0,
    },
    "Successful Transmissions": {
      count: transmissionCount?.successful ?? 0,
    },
    "Processing Transmissions": {
      count: transmissionCount?.pending ?? 0,
    },
    "Failed Transmissions": {
      count: transmissionCount?.failed ?? 0,
    },
  };

  const { isReceiving } = useSSEWithRefetch({
    requestQuery: transmissionQuery,
    threshold: 10,
    watchEvents: ["organization.extension.invoice.transmission.updated"],
  });

  return (
    <AppLayout>
      <Header title="FIRS Transmissions" description="Get insights to Firs Transmissions on here" />
      <div className="p-3 bg-light" style={{ minHeight: "100%" }}>
        <IconButton className="bg-secondary-subtle mb-3" onClick={() => navigate(-1)}>
          <ArrowBack />
        </IconButton>
        <Grid container spacing={2} mb={2}>
          {Object.keys(stats).map((key) => {
            const item = stats[key];
            return (
              <Grid item xs={12} lg={3} key={key}>
                <Paper sx={{ p: 2, borderRadius: 0.5, height: "100%" }}>
                  <Typography mb={2} color="text.secondary">
                    {key}
                  </Typography>
                  <Typography fontSize={24} fontWeight={500} sx={{ wordBreak: "break-word" }}>
                    {item?.count != null ? item.count : item?.date}
                  </Typography>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
        <div className="d-flex gap-3 align-items-center flex-wrap justify-content-between mb-3">
          <div className="d-flex gap-3 align-items-center flex-wrap">
            <div>
              <AppTextField
                placeholder="Search by invoice number/IRN"
                value={keyword}
                onChange={(e) => handleChange(e, "keyword")}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Search className="text-secondary" />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
            <SortFilterMenu
              label="Status"
              fieldLabel="Status"
              sortBy={status}
              sortOrder=""
              sortByItems={STATUS_FILTER_ITEMS}
              hideOrder
              onSortByChange={(e) => handleChange(e, "status")}
            />
            <DateRangePreset
              datePreset={datePreset}
              dateFrom={dateFrom}
              dateTo={dateTo}
              onPresetChange={handlePresetChange}
              onCustomRangeChange={handleCustomRangeChange}
              onClear={handleClearDate}
            />
          </div>
          <Tooltip title={isReceiving ? "Updates are still processing. Please wait..." : ""}>
            <span className="mb-3">
              <Button
                color="secondary"
                sx={{ whiteSpace: "nowrap" }}
                variant="outlined"
                disabled={retryMutation.isPending || isReceiving}
                startIcon={<BiRefresh />}
                onClick={() => retryMutation.mutate()}
              >
                Retry Timeline
              </Button>
            </span>
          </Tooltip>
        </div>

        {isReceiving && (
          <Alert severity="info" sx={{ mb: 3 }} variant="standard">
            We're retrying the operation. Please wait while we process your request."
          </Alert>
        )}

        <Paper sx={{ p: 2, borderRadius: 0.5 }}>
          {transmissionQuery.isPending && !invoices?.data?.length ? null : (
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: "#F6F6F6" }}>
                  <TableRow>
                    <TableCell sx={{ py: 1.5, width: 60 }}>S/N</TableCell>
                    <TableCell sx={{ py: 1.5 }}>Customer</TableCell>
                    <TableCell sx={{ py: 1.5 }}>Issue Date</TableCell>
                    <TableCell sx={{ py: 1.5 }}>IRN</TableCell>
                    <TableCell sx={{ py: 1.5 }}>Amount</TableCell>
                    <TableCell sx={{ py: 1.5 }}>Transmission Steps</TableCell>
                    <TableCell sx={{ py: 1.5 }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices?.data?.map((item, index) => {
                    const totalSteps = getCount(item);
                    const successCount = successCheck(item).successCount;
                    const progress = (successCount / totalSteps) * 100;
                    return (
                      <TableRow key={item?.id}>
                        <TableCell sx={{ py: 1.5 }}>
                          {(Math.max(1, parseInt(page, 10) || 1) - 1) * Math.max(1, parseInt(limit, 10) || 20) + index + 1}
                        </TableCell>
                        <TableCell sx={{ py: 1.5, whiteSpace: "nowrap" }}>
                          <Box component="span" display="flex" alignItems="center" gap={3}>
                            <span>{item?.billedTo?.name}</span>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 1.5, whiteSpace: "nowrap" }}>{moment(item?.issueDate).format("DD-MM-YYYY")}</TableCell>
                        <TableCell sx={{ py: 1.5 }}>{item?.integration?.firs?.irn}</TableCell>
                        <TableCell sx={{ py: 1.5, whiteSpace: "nowrap" }}>{moneyFormat(item?.total, item?.currency)}</TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Box display="flex" alignItems="center">
                            <LinearProgress
                              variant="determinate"
                              color={successCount == totalSteps ? "success" : "warning"}
                              value={progress}
                              sx={{ borderRadius: 10, height: 6, width: 120 }}
                            />
                            <Typography color="primary" fontStyle="italic" ml={2}>
                              {successCount}/{totalSteps}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography
                            onClick={() => {
                              setSelectedInvoice(item);
                              setIsOpen(true);
                            }}
                            className="text-primary click"
                          >
                            View
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <EmptyState isEmpty={!transmissionQuery.isPending && !invoices?.data?.length} />
        </Paper>
        <PreloaderTable isLoading={transmissionQuery.isPending} />
        <div className="my-3">
          <div className="d-flex align-items-center gap-4 justify-content-between">
            <div>
              <AppSelectField
                value={limit}
                placeholder="Limit"
                onChange={(e) => handleChange(e.target.value, "limit")}
                items={[
                  { label: "20", value: "20" },
                  { label: "30", value: "30" },
                  { label: "40", value: "40" },
                  { label: "50", value: "50" },
                ]}
              />
            </div>
            <div>
              <Pagination
                page={Math.max(1, parseInt(page, 10) || 1)}
                count={invoices?.totalPages || 1}
                onChange={(_, value) => {
                  handleChange(value, "page");
                }}
                shape="rounded"
                color="primary"
              />
            </div>
          </div>
        </div>
        <ViewTimelineModal invoice={selectedInvoice} isOpen={isOpen} onClose={() => setIsOpen(false)} />
      </div>
    </AppLayout>
  );

  function handleChange(e, name = "") {
    const value = name === "page" || name === "limit" ? e : e.target.value;
    searchParams.set(name, value);
    if (name !== "page") {
      // Any filter/page-size change resets to page 1 so we never land on an empty page.
      searchParams.set("page", "1");
    }
    navigate(
      {
        pathname: window.location.pathname,
        search: searchParams.toString(),
      },
      { replace: true },
      { scrollY: 0 },
    );
    if (name === "page") scrollHandler();
  }

  function handlePresetChange(preset) {
    const next = new URLSearchParams(window.location.search);
    next.set("page", "1");
    next.delete("datePreset");
    next.delete("dateFrom");
    next.delete("dateTo");
    // ALL_TIME means no date filter — no date params in the payload.
    if (preset !== DATE_PRESETS.ALL_TIME) {
      const range = computePresetRange(preset);
      next.set("datePreset", preset);
      if (range.dateFrom && range.dateTo) {
        // computePresetRange returns UTC ISO — convert to local calendar dates.
        next.set("dateFrom", moment(range.dateFrom).local().format("YYYY-MM-DD"));
        // Backend dateTo is exclusive (start of the next period).
        next.set("dateTo", moment(range.dateTo).local().format("YYYY-MM-DD"));
      }
    }
    navigate(
      {
        pathname: window.location.pathname,
        search: next.toString(),
      },
      { replace: true },
    );
  }

  function handleCustomRangeChange({ dateFrom: from, dateTo: to }) {
    const next = new URLSearchParams(window.location.search);
    next.set("datePreset", DATE_PRESETS.CUSTOM);
    next.set("dateFrom", from);
    next.set("dateTo", to);
    next.set("page", "1");
    navigate(
      {
        pathname: window.location.pathname,
        search: next.toString(),
      },
      { replace: true },
    );
  }

  function handleClearDate() {
    const next = new URLSearchParams(window.location.search);
    next.delete("datePreset");
    next.delete("dateFrom");
    next.delete("dateTo");
    next.set("page", "1");
    navigate(
      {
        pathname: window.location.pathname,
        search: next.toString(),
      },
      { replace: true },
    );
  }
};

export default TransmittedInvoices;
